/**
 * P&ID Extraction Page
 * 
 * Allows users to upload P&ID drawings (PDF), define one or more tag patterns,
 * scan the drawing for matches, and export results to Excel.
 * 
 * Pattern syntax:
 *   0 = any digit (0-9)
 *   X = any letter (A-Z)
 *   Any other character is matched literally (e.g. dashes, slashes)
 * 
 * Text-native PDFs are processed client-side with pdfjs (fast, free).
 * Image-based PDFs are sent to the backend for GPT-4 Vision OCR.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import * as pdfjs from 'pdfjs-dist';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const QUOTE_CHARS = '"\u201C\u201D\u201E\u201F\u2033\u2036\u02DD';

function patternToRegex(pattern) {
  let regex = '';
  for (const ch of pattern) {
    if (ch === '0') regex += '\\d';
    else if (ch === 'X' || ch === 'x') regex += '[A-Za-z]';
    else if (QUOTE_CHARS.includes(ch)) regex += `[${QUOTE_CHARS}]`;
    else regex += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  return regex;
}

function buildCombinedRegex(patterns) {
  const parts = patterns
    .map(p => p.trim())
    .filter(Boolean)
    .map(patternToRegex);
  if (parts.length === 0) return null;
  return new RegExp(parts.map(p => `(${p})`).join('|'), 'gi');
}

function matchToPatternLabel(matchStr, patterns) {
  for (const p of patterns) {
    const re = new RegExp(`^${patternToRegex(p)}$`, 'i');
    if (re.test(matchStr)) return p;
  }
  return '?';
}

function patternToDisplay(pattern) {
  return pattern.split('').map(ch => {
    if (ch === '0') return { char: '0', type: 'digit' };
    if (ch === 'X' || ch === 'x') return { char: ch.toUpperCase(), type: 'letter' };
    return { char: ch, type: 'literal' };
  });
}

const PidExtractionPage = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('pidExtraction', 'global', true);
  const [patterns, setPatterns] = useState(['']);
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, file: '', ocr: false });
  const [results, setResults] = useState([]);
  const [ocrPages, setOcrPages] = useState(0);
  const [extractedTexts, setExtractedTexts] = useState([]);
  const [showDebugText, setShowDebugText] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(true);
  const [pdfMode, setPdfMode] = useState('searchable');
  const [elapsedTime, setElapsedTime] = useState(0);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (scanning) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [scanning]);

  const [savedPatterns, setSavedPatterns] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const validPatterns = patterns.filter(p => p.trim().length > 0);

  useEffect(() => {
    let cancelled = false;
    const fetchPatterns = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          if (i > 0) await new Promise(r => setTimeout(r, 2000));
          const resp = await authenticatedApiCall(instance, accounts, 'GET', '/pid-extraction/patterns');
          if (!cancelled && resp.data?.success) {
            setSavedPatterns(resp.data.data || []);
          }
          return;
        } catch (err) {
          console.log(`[P&ID] Failed to load patterns (attempt ${i + 1}):`, err.message);
        }
      }
    };
    if (accounts?.length > 0) {
      fetchPatterns();
    }
    return () => { cancelled = true; };
  }, [instance, accounts]);

  const handleLoadPatternSet = (patternSet) => {
    if (patternSet?.patterns) {
      setPatterns(prev => {
        const existing = prev.filter(p => p.trim().length > 0);
        const newOnes = patternSet.patterns.filter(p => !existing.includes(p));
        const merged = [...existing, ...newOnes];
        return merged.length > 0 ? merged : [''];
      });
      setHasScanned(false);
      setResults([]);
    }
  };

  const handleSavePatternSet = async () => {
    const name = saveName.trim();
    if (!name || validPatterns.length === 0) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/pid-extraction/patterns', {
        name,
        patterns: validPatterns,
      });
      if (resp.data?.success) {
        setSavedPatterns(prev => [resp.data.data, ...prev]);
        setSaveName('');
        setShowSaveInput(false);
      }
    } catch (err) {
      console.error('Failed to save pattern set:', err);
      alert('Failed to save pattern set.');
    }
  };

  const handleDeletePatternSet = async (id) => {
    if (!window.confirm('Delete this saved pattern set?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/pid-extraction/patterns/${id}`);
      setSavedPatterns(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Failed to delete pattern set:', err);
    }
  };

  const updatePattern = (idx, value) => {
    setPatterns(prev => prev.map((p, i) => i === idx ? value : p));
    setHasScanned(false);
    setResults([]);
  };

  const addPattern = () => setPatterns(prev => [...prev, '']);

  const removePattern = (idx) => {
    setPatterns(prev => prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx));
    setHasScanned(false);
    setResults([]);
  };

  const handleFileSelect = useCallback((selectedFiles) => {
    const pdfFiles = Array.from(selectedFiles).filter(
      f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfFiles.length > 0) {
      setFiles(prev => [...prev, ...pdfFiles]);
      setHasScanned(false);
      setResults([]);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const removeFile = useCallback((idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
    setHasScanned(false);
    setResults([]);
  }, []);

  const handleScan = async () => {
    if (validPatterns.length === 0 || files.length === 0) return;

    const regex = buildCombinedRegex(validPatterns);
    if (!regex) return;

    setScanning(true);
    setResults([]);
    setHasScanned(false);
    setOcrPages(0);
    setExtractedTexts([]);

    const allMatches = [];
    let ocrPagesUsed = 0;

    if (pdfMode === 'searchable') {
      // Fast client-side text extraction + regex matching
      let totalPages = 0;
      for (const f of files) {
        const data = await f.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        totalPages += pdf.numPages;
      }
      setProgress({ current: 0, total: totalPages, file: '', ocr: false });
      let pagesDone = 0;

      for (const f of files) {
        try {
          const data = await f.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data }).promise;

          for (let p = 1; p <= pdf.numPages; p++) {
            setProgress({ current: pagesDone + 1, total: totalPages, file: f.name, ocr: false });
            const page = await pdf.getPage(p);
            const tc = await page.getTextContent();
            const text = tc.items.map(item => item.str).join(' ').trim();

            setExtractedTexts(prev => [...prev, { file: f.name, page: p, text: text.substring(0, 3000), isOcr: false }]);

            let match;
            regex.lastIndex = 0;
            while ((match = regex.exec(text)) !== null) {
              allMatches.push({ tag: match[0], page: p, sourceFile: f.name, pattern: matchToPatternLabel(match[0], validPatterns) });
            }

            const lines = text.split('\n');
            for (const line of lines) {
              const stripped = line.replace(/\s+/g, '');
              if (stripped.length < 5) continue;
              regex.lastIndex = 0;
              while ((match = regex.exec(stripped)) !== null) {
                if (!allMatches.some(m => m.tag === match[0] && m.page === p && m.sourceFile === f.name)) {
                  allMatches.push({ tag: match[0], page: p, sourceFile: f.name, pattern: matchToPatternLabel(match[0], validPatterns) });
                }
              }
            }
            pagesDone++;
          }
        } catch (err) {
          console.error(`Failed to process ${f.name}:`, err);
        }
      }
    } else {
      // Non-searchable: AI Vision OCR via backend
      const totalPages = files.length; // 1 API call per file
      setProgress({ current: 0, total: totalPages, file: '', ocr: true });

      for (let fi = 0; fi < files.length; fi++) {
        const f = files[fi];
        setProgress({ current: fi + 1, total: totalPages, file: f.name, ocr: true });

        const formData = new FormData();
        formData.append('pdf', f);
        formData.append('patterns', JSON.stringify(validPatterns));

        try {
          const ocrResp = await authenticatedApiCall(
            instance, accounts, 'POST',
            '/pid-extraction/ocr',
            formData,
            { timeout: 300000 }
          );

          if (ocrResp.data?.success) {
            const ocrTags = ocrResp.data.tags || [];
            ocrPagesUsed += ocrResp.data.pagesProcessed || 1;

            for (const tag of ocrTags) {
              const tagStr = typeof tag === 'string' ? tag : tag.tag;
              const tagPage = typeof tag === 'string' ? 1 : (tag.page || 1);
              const label = matchToPatternLabel(tagStr, validPatterns);
              if (!allMatches.some(m => m.tag === tagStr && m.page === tagPage && m.sourceFile === f.name)) {
                allMatches.push({ tag: tagStr, page: tagPage, sourceFile: f.name, pattern: label !== '?' ? label : 'AI Vision' });
              }
            }

            setExtractedTexts(prev => [...prev, {
              file: f.name, page: 1,
              text: `(AI Vision OCR — ${ocrTags.length} tags found across ${ocrResp.data.pagesProcessed || 1} pages)`,
              isOcr: true,
            }]);
          } else {
            alert(`OCR failed for ${f.name}: ${ocrResp.data?.error || 'Unknown error'}`);
          }
        } catch (ocrErr) {
          alert(`OCR API error for ${f.name}: ${ocrErr.response?.data?.error || ocrErr.message || 'Backend may be down'}`);
        }
      }
    }

    setOcrPages(ocrPagesUsed);
    setResults(allMatches);
    setHasScanned(true);
    setScanning(false);
  };

  const displayResults = showDuplicates
    ? results
    : results.filter((r, i, arr) => arr.findIndex(x => x.tag === r.tag) === i);

  const handleExport = async () => {
    if (displayResults.length === 0) return;
    setExporting(true);
    try {
      const resp = await authenticatedApiCall(
        instance, accounts, 'POST',
        '/pid-extraction/export',
        {
          matches: displayResults,
          pattern: validPatterns.join(' | '),
          filename: files.length === 1 ? files[0].name : `${files.length}-files`,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = files.length === 1
        ? `pid-tags-${files[0].name.replace(/\.pdf$/i, '')}.xlsx`
        : `pid-tags-${files.length}-files.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Excel export failed. Downloading as CSV instead.');
      exportCsvFallback();
    } finally {
      setExporting(false);
    }
  };

  const exportCsvFallback = () => {
    const header = '#,Tag,Page,Source File,Matched Pattern\n';
    const rows = displayResults.map((m, i) =>
      `${i + 1},"${m.tag}",${m.page},"${m.sourceFile}","${m.pattern}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pid-tags-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const hasMultiplePatterns = validPatterns.length > 1;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.title}>P&ID Tag Extraction</h2>
            <p style={styles.subtitle}>
              Upload P&ID drawings and extract tags matching one or more custom patterns
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={() => setShowHelp(true)} style={styles.helpBtn} title="How to use this tool">
              ?
            </button>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div style={styles.modalOverlay} onClick={() => setShowHelp(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>How to Use P&ID Tag Extraction</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="pid-extraction-help" title="P&ID Tag Extraction Help" accent="#2E5090" />
                <button onClick={() => setShowHelp(false)} style={styles.modalClose}>&times;</button>
              </div>
            </div>
            <div style={styles.modalBody}>
              <HelpWithVideoTour guideId="pid-extraction-help" title="P&ID Tag Extraction Help" accent="#2E5090">
              <div style={styles.helpSection}>
                <h4 style={styles.helpHeading}>Step 1: Define Patterns</h4>
                <p style={styles.helpText}>
                  Patterns describe the format of tags in your drawings. Use these wildcards:
                </p>
                <div style={styles.helpGrid}>
                  <div style={styles.helpGridItem}>
                    <span style={{ ...styles.helpToken, background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd' }}>0</span>
                    <span>Any digit (0-9)</span>
                  </div>
                  <div style={styles.helpGridItem}>
                    <span style={{ ...styles.helpToken, background: '#d1fae5', color: '#059669', border: '1px solid #6ee7b7' }}>X</span>
                    <span>Any letter (A-Z)</span>
                  </div>
                  <div style={styles.helpGridItem}>
                    <span style={{ ...styles.helpToken, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }}>- / "</span>
                    <span>Matched literally</span>
                  </div>
                </div>
                <div style={styles.helpExamples}>
                  <strong>Examples:</strong>
                  <table style={styles.helpTable}>
                    <tbody>
                      <tr><td style={styles.helpTd}><code>X0-0"-XXX-X00X-000</code></td><td style={styles.helpTd}>Matches: Z4-6"-CGL-C22F-055</td></tr>
                      <tr><td style={styles.helpTd}><code>X0-0/0"-XXX-X00X-000</code></td><td style={styles.helpTd}>Matches: Z4-3/4"-WPR-C21A-065</td></tr>
                      <tr><td style={styles.helpTd}><code>0000-XX-00</code></td><td style={styles.helpTd}>Matches: 1234-AB-56</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={styles.helpSection}>
                <h4 style={styles.helpHeading}>Step 2: Upload Drawings</h4>
                <p style={styles.helpText}>
                  Drag and drop or browse to upload one or more PDF files. You can process multiple drawings at once.
                </p>
              </div>
              <div style={styles.helpSection}>
                <h4 style={styles.helpHeading}>Step 3: Choose Mode & Scan</h4>
                <div style={styles.helpModes}>
                  <div style={styles.helpModeCard}>
                    <strong style={{ color: '#2E5090' }}>Searchable PDF</strong>
                    <p style={styles.helpModeDesc}>For PDFs with selectable text. Instant results, very reliable. The text is extracted directly from the PDF structure.</p>
                  </div>
                  <div style={styles.helpModeCard}>
                    <strong style={{ color: '#d97706' }}>Non-Searchable (AI Vision)</strong>
                    <p style={styles.helpModeDesc}>For scanned or image-based PDFs. Uses AI to read drawings visually. Takes 30-90 seconds per page but can read any image.</p>
                  </div>
                </div>
              </div>
              <div style={styles.helpSection}>
                <h4 style={styles.helpHeading}>Tips</h4>
                <ul style={styles.helpList}>
                  <li>Use the <strong>Pattern Library</strong> dropdown to quickly load commonly used formats</li>
                  <li>Save your patterns for reuse — they're shared across the team</li>
                  <li>You can load multiple pattern sets — they'll be combined (no duplicates)</li>
                  <li>Export results to Excel for further processing</li>
                </ul>
              </div>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {/* Step 1 — Patterns */}
      <div style={styles.card}>
        <div style={styles.stepBadge}>1</div>
        <h3 style={styles.cardTitle}>Define Tag Pattern(s)</h3>
        <p style={styles.cardDesc}>
          Use <strong>0</strong> for any digit and <strong>X</strong> for any letter.
          All other characters are matched literally.
          Add multiple patterns to match different tag formats in one scan.
        </p>

        {/* Pattern Library Dropdown */}
        {savedPatterns.length > 0 && (
          <div style={styles.patternLibrary}>
            <label style={styles.savedLabel}>Pattern Library:</label>
            <div style={styles.libraryRow}>
              <select
                style={styles.librarySelect}
                defaultValue=""
                onChange={e => {
                  const val = e.target.value;
                  if (!val) return;
                  if (val === '__all__') {
                    const allPats = [...new Set(savedPatterns.flatMap(sp => sp.patterns))];
                    setPatterns(prev => {
                      const existing = prev.filter(p => p.trim().length > 0);
                      const newOnes = allPats.filter(p => !existing.includes(p));
                      return [...existing, ...newOnes].length > 0 ? [...existing, ...newOnes] : [''];
                    });
                  } else if (val.startsWith('set:')) {
                    const setId = parseInt(val.split(':')[1]);
                    const sp = savedPatterns.find(s => s.id === setId);
                    if (sp) handleLoadPatternSet(sp);
                  } else {
                    setPatterns(prev => {
                      const existing = prev.filter(p => p.trim().length > 0);
                      if (existing.includes(val)) return prev;
                      return [...existing, val];
                    });
                  }
                  e.target.value = '';
                  setHasScanned(false);
                  setResults([]);
                }}
              >
                <option value="">Select a pattern or set to add...</option>
                <optgroup label="Pattern Sets">
                  {savedPatterns.map(sp => (
                    <option key={`set:${sp.id}`} value={`set:${sp.id}`}>
                      {sp.name} ({sp.patterns.length} patterns)
                    </option>
                  ))}
                  <option value="__all__">Load All Patterns</option>
                </optgroup>
                <optgroup label="Individual Patterns">
                  {[...new Set(savedPatterns.flatMap(sp => sp.patterns))].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div style={styles.savedChips}>
              {savedPatterns.map(sp => (
                <div key={sp.id} style={styles.savedChip}>
                  <button
                    onClick={() => handleLoadPatternSet(sp)}
                    style={styles.chipBtn}
                    title={sp.patterns.join(', ')}
                  >
                    {sp.name}
                  </button>
                  <button
                    onClick={() => handleDeletePatternSet(sp.id)}
                    style={styles.chipDelete}
                    title="Delete"
                  >&times;</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.patternsContainer}>
          {patterns.map((pat, idx) => {
            const tokens = pat ? patternToDisplay(pat) : [];
            return (
              <div key={idx} style={styles.patternBlock}>
                <div style={styles.patternRow}>
                  <span style={styles.patternIndex}>{idx + 1}</span>
                  <input
                    type="text"
                    value={pat}
                    onChange={e => updatePattern(idx, e.target.value)}
                    placeholder={idx === 0 ? 'e.g.  0000-XX-00' : 'e.g.  0000-XX-0'}
                    style={styles.patternInput}
                    spellCheck={false}
                  />
                  <button
                    style={styles.patternRemoveBtn}
                    onClick={() => removePattern(idx)}
                    title="Remove pattern"
                    disabled={patterns.length <= 1 && !pat}
                  >
                    &times;
                  </button>
                </div>
                {tokens.length > 0 && (
                  <div style={styles.inlinePreview}>
                    {tokens.map((t, i) => (
                      <span key={i} style={{
                        ...styles.tokenSmall,
                        ...(t.type === 'digit' ? styles.tokenDigit : t.type === 'letter' ? styles.tokenLetter : styles.tokenLiteral),
                      }}>
                        {t.type === 'digit' ? '#' : t.type === 'letter' ? 'A' : t.char}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={styles.patternActions}>
          <button onClick={addPattern} style={styles.addPatternBtn}>+ Add Pattern</button>

          {validPatterns.length > 0 && !showSaveInput && (
            <button onClick={() => setShowSaveInput(true)} style={styles.savePatternBtn}>
              Save Patterns
            </button>
          )}

          {showSaveInput && (
            <div style={styles.saveInputRow}>
              <input
                type="text"
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                placeholder="Name this pattern set..."
                style={styles.saveNameInput}
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleSavePatternSet()}
              />
              <button onClick={handleSavePatternSet} style={styles.saveConfirmBtn} disabled={!saveName.trim()}>
                Save
              </button>
              <button onClick={() => { setShowSaveInput(false); setSaveName(''); }} style={styles.saveCancelBtn}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div style={styles.legend}>
          <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#3b82f6' }} /> # = Digit (0-9)</span>
          <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#10b981' }} /> A = Letter (A-Z)</span>
          <span style={styles.legendItem}><span style={{ ...styles.legendDot, backgroundColor: '#6b7280' }} /> Gray = Literal</span>
        </div>
      </div>

      {/* Step 2 — Upload */}
      <div style={styles.card}>
        <div style={styles.stepBadge}>2</div>
        <h3 style={styles.cardTitle}>Upload P&ID Drawings</h3>

        <div
          style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}) }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={e => { handleFileSelect(e.target.files); e.target.value = ''; }}
          />
          <div style={styles.dropIcon}>&#128196;</div>
          <p style={styles.dropText}>
            Drag & drop PDF files here, or <span style={styles.browseLink}>browse</span>
          </p>
          <p style={styles.dropHint}>PDF files accepted — image-based PDFs are processed with AI Vision OCR</p>
        </div>

        {files.length > 0 && (
          <div style={styles.fileList}>
            {files.map((f, i) => (
              <div key={i} style={styles.fileItem}>
                <span style={styles.fileName}>{f.name}</span>
                <span style={styles.fileSize}>{(f.size / 1024).toFixed(0)} KB</span>
                <button style={styles.removeBtn} onClick={() => removeFile(i)} title="Remove file">&times;</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Step 3 — Scan */}
      <div style={styles.card}>
        <div style={styles.stepBadge}>3</div>
        <h3 style={styles.cardTitle}>Scan & Extract</h3>

        <div style={styles.modeToggle}>
          <button
            onClick={() => setPdfMode('searchable')}
            style={{ ...styles.modeBtn, ...(pdfMode === 'searchable' ? styles.modeBtnActive : {}) }}
          >
            Searchable PDF
          </button>
          <button
            onClick={() => setPdfMode('image')}
            style={{ ...styles.modeBtn, ...(pdfMode === 'image' ? styles.modeBtnActive : {}) }}
          >
            Non-Searchable (AI Vision)
          </button>
        </div>
        <p style={styles.modeHint}>
          {pdfMode === 'searchable'
            ? 'Fast text extraction — use for PDFs with selectable text.'
            : 'AI-powered OCR — use for scanned/image-based PDFs (slower, but reads drawings).'}
        </p>

        <button
          onClick={handleScan}
          disabled={scanning || validPatterns.length === 0 || files.length === 0}
          style={{
            ...styles.scanBtn,
            ...(scanning || validPatterns.length === 0 || files.length === 0 ? styles.scanBtnDisabled : {}),
          }}
        >
          {scanning ? 'Scanning...' : `Scan Drawings (${validPatterns.length} pattern${validPatterns.length !== 1 ? 's' : ''})`}
        </button>

        {scanning && (
          <div style={styles.progressSection}>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${(progress.current / progress.total) * 100}%` }} />
            </div>
            <p style={styles.progressText}>
              Page {progress.current} / {progress.total} &mdash; {progress.file}
              {progress.ocr && <span style={styles.ocrBadge}>AI Vision OCR</span>}
              <span style={styles.timer}>
                {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}
              </span>
            </p>
          </div>
        )}

        {hasScanned && (
          <div style={styles.resultsSummary}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{results.length}</div>
              <div style={styles.statLabel}>Total Matches</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{new Set(results.map(r => r.tag)).size}</div>
              <div style={styles.statLabel}>Unique Tags</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{files.length}</div>
              <div style={styles.statLabel}>Files Scanned</div>
            </div>
            {ocrPages > 0 && (
              <div style={{ ...styles.statCard, background: '#fef3c7', borderColor: '#fbbf24' }}>
                <div style={{ ...styles.statNumber, color: '#d97706' }}>{ocrPages}</div>
                <div style={styles.statLabel}>OCR Pages</div>
              </div>
            )}
            {hasMultiplePatterns && (
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{new Set(results.map(r => r.pattern)).size}</div>
                <div style={styles.statLabel}>Patterns Matched</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {hasScanned && results.length > 0 && (
        <div style={styles.card}>
          <div style={styles.resultsHeader}>
            <h3 style={styles.cardTitle}>Results</h3>
            <div style={styles.resultsActions}>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={!showDuplicates}
                  onChange={() => setShowDuplicates(v => !v)}
                  style={styles.checkbox}
                />
                Hide duplicates
              </label>
              <button
                onClick={handleExport}
                disabled={exporting}
                style={styles.exportBtn}
              >
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 50 }}>#</th>
                  <th style={styles.th}>Tag</th>
                  <th style={{ ...styles.th, width: 80 }}>Page</th>
                  <th style={styles.th}>Source File</th>
                  {hasMultiplePatterns && <th style={styles.th}>Matched Pattern</th>}
                </tr>
              </thead>
              <tbody>
                {displayResults.map((m, i) => (
                  <tr key={i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                    <td style={styles.td}>{i + 1}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 600 }}>{m.tag}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{m.page}</td>
                    <td style={styles.td}>{m.sourceFile}</td>
                    {hasMultiplePatterns && (
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, color: '#6366f1' }}>{m.pattern}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={styles.resultCount}>
            Showing {displayResults.length} {showDuplicates ? 'matches' : 'unique tags'}
          </p>
        </div>
      )}

      {hasScanned && results.length === 0 && (
        <div style={styles.card}>
          <div style={styles.noResults}>
            <div style={{ fontSize: 48 }}>&#128269;</div>
            <h3 style={{ margin: '12px 0 4px' }}>No Matches Found</h3>
            <p style={{ color: '#6b7280' }}>
              No tags matching {validPatterns.length === 1 ? 'the pattern' : 'any of the patterns'}{' '}
              {validPatterns.map((p, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <code style={styles.code}>{p}</code>
                </span>
              ))}{' '}
              were found in the uploaded drawings. Double-check the patterns and try again.
            </p>
          </div>
        </div>
      )}

      {hasScanned && extractedTexts.length > 0 && (
        <div style={styles.card}>
          <div style={styles.resultsHeader}>
            <h3 style={styles.cardTitle}>Extracted Text (Debug)</h3>
            <button
              onClick={() => setShowDebugText(v => !v)}
              style={{ ...styles.exportBtn, background: '#6366f1' }}
            >
              {showDebugText ? 'Hide' : 'Show'} Raw Text
            </button>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b' }}>
            View the raw text extracted from each page to verify OCR quality and adjust patterns.
          </p>
          {showDebugText && extractedTexts.map((et, idx) => (
            <div key={idx} style={{ marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <strong>{et.file}</strong> &mdash; Page {et.page}
                {et.isOcr && <span style={styles.ocrBadge}>OCR</span>}
                <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>{et.text.length} chars</span>
              </div>
              <pre style={{ margin: 0, padding: 12, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 300, overflow: 'auto', background: '#fafbfc', color: '#334155' }}>
                {et.text || '(empty)'}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: 960, margin: '0 auto', padding: '24px 16px' },
  header: { marginBottom: 24 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' },
  subtitle: { margin: '4px 0 0', fontSize: 14, color: '#64748b' },

  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '24px 24px 20px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  stepBadge: {
    position: 'absolute', top: -12, left: 20,
    width: 26, height: 26, borderRadius: '50%',
    background: '#2E5090', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700,
  },
  cardTitle: { margin: '0 0 4px', fontSize: 16, fontWeight: 600, color: '#1e293b' },
  cardDesc: { margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.5 },

  patternsContainer: { display: 'flex', flexDirection: 'column', gap: 10 },
  patternBlock: { display: 'flex', flexDirection: 'column', gap: 4 },
  patternRow: { display: 'flex', gap: 8, alignItems: 'center' },
  patternIndex: {
    width: 22, height: 22, borderRadius: '50%',
    background: '#f1f5f9', color: '#64748b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  patternInput: {
    flex: 1, padding: '10px 14px', fontSize: 16, fontFamily: 'monospace',
    border: '2px solid #e2e8f0', borderRadius: 8, outline: 'none',
    transition: 'border-color 0.15s',
    letterSpacing: 2,
  },
  patternRemoveBtn: {
    width: 30, height: 30, borderRadius: 6,
    background: 'none', border: '1px solid #e2e8f0',
    fontSize: 18, color: '#94a3b8', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, lineHeight: 1,
  },

  inlinePreview: { display: 'flex', gap: 2, marginLeft: 30, flexWrap: 'wrap' },
  tokenSmall: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 24, borderRadius: 3, fontSize: 12, fontWeight: 700, fontFamily: 'monospace',
  },
  tokenDigit: { background: '#dbeafe', color: '#2563eb', border: '1px solid #93c5fd' },
  tokenLetter: { background: '#d1fae5', color: '#059669', border: '1px solid #6ee7b7' },
  tokenLiteral: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },

  patternActions: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' },
  addPatternBtn: {
    padding: '6px 16px', fontSize: 13, fontWeight: 600,
    color: '#2E5090', background: '#eff6ff', border: '1px dashed #93b4d4',
    borderRadius: 6, cursor: 'pointer',
  },
  savePatternBtn: {
    padding: '6px 16px', fontSize: 13, fontWeight: 600,
    color: '#059669', background: '#ecfdf5', border: '1px dashed #6ee7b7',
    borderRadius: 6, cursor: 'pointer',
  },
  saveInputRow: { display: 'flex', gap: 6, alignItems: 'center', flex: 1, minWidth: 240 },
  saveNameInput: {
    flex: 1, padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db',
    borderRadius: 6, outline: 'none',
  },
  saveConfirmBtn: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    color: '#fff', background: '#059669', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  saveCancelBtn: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer',
  },

  savedLabel: { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 8 },

  legend: { display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' },
  legendItem: { fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 },
  legendDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },

  dropZone: {
    border: '2px dashed #cbd5e1', borderRadius: 10, padding: '36px 24px',
    textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s',
    background: '#fafbfc',
  },
  dropZoneActive: {
    borderColor: '#2E5090', background: '#eff6ff',
  },
  dropIcon: { fontSize: 40, marginBottom: 8 },
  dropText: { margin: 0, fontSize: 14, color: '#475569' },
  browseLink: { color: '#2E5090', fontWeight: 600, textDecoration: 'underline' },
  dropHint: { margin: '6px 0 0', fontSize: 12, color: '#94a3b8' },

  fileList: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 },
  fileItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0',
  },
  fileName: { flex: 1, fontSize: 13, fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize: { fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' },
  removeBtn: {
    background: 'none', border: 'none', fontSize: 18, color: '#94a3b8',
    cursor: 'pointer', padding: '0 4px', lineHeight: 1,
  },

  modeToggle: {
    display: 'flex', gap: 0, marginBottom: 8, borderRadius: 8, overflow: 'hidden',
    border: '1px solid #d1d5db', width: 'fit-content',
  },
  modeBtn: {
    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: '#f9fafb', color: '#6b7280', transition: 'all 0.15s',
  },
  modeBtnActive: {
    background: '#2E5090', color: '#fff',
  },
  modeHint: {
    fontSize: 12, color: '#6b7280', margin: '0 0 12px 0', fontStyle: 'italic',
  },
  scanBtn: {
    padding: '10px 28px', fontSize: 15, fontWeight: 600, color: '#fff',
    background: '#2E5090', border: 'none', borderRadius: 8, cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  scanBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  progressSection: { marginTop: 16 },
  progressBar: { height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', background: '#2E5090', borderRadius: 3, transition: 'width 0.2s' },
  progressText: { marginTop: 6, fontSize: 12, color: '#64748b' },
  ocrBadge: {
    marginLeft: 8, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    background: '#fef3c7', color: '#d97706', borderRadius: 4, border: '1px solid #fbbf24',
  },
  timer: {
    marginLeft: 12, fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#2E5090',
  },

  resultsSummary: { display: 'flex', gap: 16, marginTop: 18, flexWrap: 'wrap' },
  statCard: {
    flex: 1, minWidth: 120, textAlign: 'center',
    padding: '14px 10px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd',
  },
  statNumber: { fontSize: 28, fontWeight: 700, color: '#0369a1' },
  statLabel: { fontSize: 12, color: '#64748b', marginTop: 2 },

  resultsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 },
  resultsActions: { display: 'flex', alignItems: 'center', gap: 16 },
  toggleLabel: { fontSize: 13, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
  checkbox: { accentColor: '#2E5090' },
  exportBtn: {
    padding: '8px 20px', fontSize: 13, fontWeight: 600, color: '#fff',
    background: '#059669', border: 'none', borderRadius: 6, cursor: 'pointer',
  },

  tableWrapper: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 12,
    textTransform: 'uppercase', letterSpacing: 0.5,
    background: '#f1f5f9', color: '#475569', borderBottom: '2px solid #e2e8f0',
  },
  td: { padding: '8px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  rowEven: { background: '#fff' },
  rowOdd: { background: '#fafbfc' },
  resultCount: { marginTop: 8, fontSize: 12, color: '#94a3b8', textAlign: 'right' },

  noResults: { textAlign: 'center', padding: '24px 0' },
  code: { background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 13, fontFamily: 'monospace' },

  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  helpBtn: {
    width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#eff6ff', border: '2px solid #2E5090', color: '#2E5090',
    fontSize: 18, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
  },

  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalContent: {
    background: '#fff', borderRadius: 16, maxWidth: 680, width: '100%',
    maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1,
  },
  modalTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' },
  modalClose: {
    width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f1f5f9', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer',
  },
  modalBody: { padding: '20px 24px' },

  helpSection: { marginBottom: 20 },
  helpHeading: { margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: '#1e293b' },
  helpText: { margin: '0 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.5 },
  helpGrid: { display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  helpGridItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569' },
  helpToken: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: 4, fontSize: 14, fontWeight: 700, fontFamily: 'monospace',
  },
  helpExamples: { background: '#f8fafc', borderRadius: 8, padding: '12px 14px', fontSize: 13 },
  helpTable: { width: '100%', borderCollapse: 'collapse', marginTop: 6 },
  helpTd: { padding: '4px 10px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9' },
  helpModes: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  helpModeCard: { flex: 1, minWidth: 200, padding: '12px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fafbfc' },
  helpModeDesc: { margin: '6px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  helpList: { margin: 0, paddingLeft: 18, fontSize: 13, color: '#475569', lineHeight: 1.8 },

  patternLibrary: { marginBottom: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' },
  libraryRow: { marginBottom: 8 },
  librarySelect: {
    width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #d1d5db',
    borderRadius: 6, background: '#fff', color: '#334155', cursor: 'pointer',
  },
  savedChips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  savedChip: { display: 'flex', alignItems: 'center', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0' },
  chipBtn: {
    padding: '5px 10px', fontSize: 12, fontWeight: 600, background: '#fff',
    border: 'none', color: '#2E5090', cursor: 'pointer',
  },
  chipDelete: {
    padding: '5px 7px', fontSize: 14, background: '#f8fafc', border: 'none',
    borderLeft: '1px solid #e2e8f0', color: '#94a3b8', cursor: 'pointer',
  },
};

export default PidExtractionPage;
